import { z } from 'zod'

import { isSafeAnnouncementColor, isSafeModalBackgroundPosition } from '@gto/announcement-ui'
import type { AnnouncementModalBackgroundSchema } from '@gto/api-client/admin/react-query'

const colorSchema = z
  .string()
  .transform((value) => value.trim())
  .refine((value) => value === '' || isSafeAnnouncementColor(value), {
    message: 'Use a hex, rgb or rgba colour.',
  })

const angleSchema = z
  .string()
  .transform((value) => value.trim())
  .refine(
    (value) => {
      const angle = Number(value)

      return value !== '' && Number.isInteger(angle) && angle >= 0 && angle <= 360
    },
    { message: 'Angle must be a whole number from 0 to 360.' },
  )
  .transform(Number)

const positionSchema = z
  .string()
  .transform((value) => value.trim())
  .refine(isSafeModalBackgroundPosition, {
    message: 'Use a listed position or an x% y% pair.',
  })

export const modalBackgroundFormSchema = z
  .object({
    modalBackgroundStart: colorSchema,
    modalBackgroundEnd: colorSchema,
    modalBackgroundAngle: angleSchema,
    modalBackgroundSize: z.enum(['cover', 'contain', 'auto']),
    modalBackgroundPosition: positionSchema,
    modalBackgroundRepeat: z.enum(['no-repeat', 'repeat', 'repeat-x', 'repeat-y']),
    modalBackgroundMobileMode: z.enum(['none', 'desktop', 'mobile']),
  })
  .transform(
    ({
      modalBackgroundStart,
      modalBackgroundEnd,
      modalBackgroundAngle,
      modalBackgroundSize,
      modalBackgroundPosition,
      modalBackgroundRepeat,
      modalBackgroundMobileMode,
    }): AnnouncementModalBackgroundSchema => ({
      start: modalBackgroundStart || null,
      end: modalBackgroundEnd || null,
      angle: modalBackgroundAngle,
      size: modalBackgroundSize,
      position: modalBackgroundPosition,
      repeat: modalBackgroundRepeat,
      mobile_mode: modalBackgroundMobileMode,
    }),
  )

type ModalBackgroundErrorField =
  | 'modalBackgroundStart'
  | 'modalBackgroundEnd'
  | 'modalBackgroundAngle'
  | 'modalBackgroundPosition'

export const modalBackgroundFormErrors = (
  values: unknown,
): Partial<Record<ModalBackgroundErrorField, string>> => {
  const result = modalBackgroundFormSchema.safeParse(values)
  if (result.success) {
    return {}
  }

  const errors: Partial<Record<ModalBackgroundErrorField, string>> = {}
  for (const issue of result.error.issues) {
    const field = issue.path[0]
    if (
      field === 'modalBackgroundStart' ||
      field === 'modalBackgroundEnd' ||
      field === 'modalBackgroundAngle' ||
      field === 'modalBackgroundPosition'
    ) {
      errors[field] = issue.message
    }
  }

  return errors
}

export const modalBackgroundFromFormValues = (values: unknown): AnnouncementModalBackgroundSchema =>
  modalBackgroundFormSchema.parse(values)

export const safeModalBackgroundFromFormValues = (
  values: unknown,
): AnnouncementModalBackgroundSchema | null => {
  const result = modalBackgroundFormSchema.safeParse(values)

  return result.success ? result.data : null
}
